import { useEffect, useMemo } from "react";
import { HtmlPortalNode, createHtmlPortalNode } from "react-reverse-portal";

import { WellCardEmbed, useWellCardPanelStore } from "stores/wellCardPanel";

export interface EmbedScreenshotSheet {
  /** Holds the embed's DOM wherever it is shown, so a move is not a remount. */
  readonly node: HtmlPortalNode;
  /** The embed is in the card's fullscreen sheet rather than in the card. */
  readonly inSheet: boolean;
}

/**
 * Take the card's fullscreen sheet while this embed is in screenshot mode.
 *
 * A dashboard widget answers a screenshot by going fullscreen - the pane moves
 * it into a sheet over the grid, and the capture reads it from there. The
 * card's embeds are not widgets and have no such pane, so the card puts up the
 * same sheet and each embed asks for it in turn.
 *
 * `inSheet` is read back out of the store rather than taken from
 * `screenshotActive`, so the embed leaves its place in the card in the SAME
 * render as the card puts the sheet up. Driven from the flag alone the two
 * would land one frame apart, with the embed's DOM detached in between and the
 * viewers measuring a container that is not on screen.
 */
export function useEmbedScreenshotSheet(
  embed: WellCardEmbed,
  screenshotActive: boolean
): EmbedScreenshotSheet {
  // 100% of whichever host it lands in: the card's own viewer or chart area on
  // the way out, and `.fullscreen > div` in the sheet.
  const node = useMemo(
    () =>
      createHtmlPortalNode({
        attributes: { style: "width: 100%; height: 100%; min-height: 0;" }
      }),
    []
  );

  const setScreenshotEmbed = useWellCardPanelStore((s) => s.setScreenshotEmbed);
  const inSheet = useWellCardPanelStore((s) => s.screenshotEmbed === embed);

  useEffect(() => {
    setScreenshotEmbed(embed, screenshotActive ? node : null);
    // Closing the card, switching tab or switching viewer all unmount the embed
    // while the sheet is up. Releasing here rather than at each of those keeps
    // the card from being left hidden behind a sheet with nothing in it.
    return () => setScreenshotEmbed(embed, null);
  }, [embed, screenshotActive, node, setScreenshotEmbed]);

  return { node, inSheet };
}
